import React from 'react';
import { HelpCircle, Shield, DollarSign, TrendingUp, FileText, Users, AlertTriangle, Clock, MapPin, CheckCircle } from 'lucide-react';
import './Home.css'; // Assuming shared styles

const FAQ = () => {
  const faqs = [
    {
      question: "What is fractional ownership in real estate?",
      answer: "Fractional ownership allows multiple investors to co-own a large property by dividing it into smaller, fixed-size units (fractions). Each investor holds legal rights to their share through a Special Purpose Vehicle (SPV), such as an LLP or private limited company.",
      icon: HelpCircle
    },
    {
      question: "How is ownership structured legally?",
      answer: "The property is purchased and held by an SPV (LLP/Private Limited Company). Investors are allotted shares or units in the SPV, which directly owns the land. This structure ensures legal ownership, transparency, and limited liability.",
      icon: FileText
    },
    {
      question: "What is the minimum investment?",
      answer: "The minimum investment is one fraction. Each property defines its own fraction size and price. You can buy multiple fractions — there's no upper limit.",
      icon: DollarSign
    },
    {
      question: "How do I make an investment?",
      answer: "Register on the platform and complete KYC (PAN, Aadhaar, bank details). Choose a property and select the number of fractions. Pay securely via escrow account (through UPI, NetBanking, Card). Receive a digital ownership certificate and legal documents for your fractions.",
      icon: CheckCircle
    },
    {
      question: "Where does my money go?",
      answer: "Investor funds are deposited in an escrow account managed by a regulated bank. The money is released only for the property purchase and related legal/transaction costs. This ensures safety and accountability.",
      icon: Shield
    },
    {
      question: "Can I sell my fractions later?",
      answer: "Yes. You can sell fractions on the secondary resale marketplace within the platform. A fee applies to resale and is shown before you confirm. Ownership records are updated in real time after every transfer.",
      icon: TrendingUp
    },
    {
      question: "How do I earn returns?",
      answer: "There are two ways: Exit Sale → When the SPV sells the land to a builder, developer, or DDA Land Pooling, profits are distributed among all investors proportionate to their holding. Resale → You may sell fractions to other investors before the final exit, potentially at a higher price.",
      icon: DollarSign
    },
    {
      question: "What fees does the platform charge?",
      answer: "The fees that apply depend on the type of deal and are stated on each project's deal page. Project expenses are recovered at cost and shown to shareholders.",
      icon: DollarSign
    },
    {
      question: "How safe is fractional ownership?",
      answer: "Real estate is a tangible asset, historically considered more stable than volatile investments. Strengths: Tangibility, capital appreciation, inflation hedge. Risks: Market fluctuations, regulatory changes, project execution delays, liquidity constraints. Always review documents and invest as per your risk appetite.",
      icon: Shield
    },
    {
      question: "What if the project fails or land purchase doesn't go through?",
      answer: "If the SPV fails to complete the purchase or clear land title, your token/initial payment is refunded from escrow (with applicable interest as per agreement).",
      icon: AlertTriangle
    },
    {
      question: "Who manages the land and paperwork?",
      answer: "The SPV/LLP created for each property manages land records, legal filings, and eventual sale. The platform oversees compliance, updates investors, and facilitates transactions.",
      icon: Users
    },
    {
      question: "Are returns guaranteed?",
      answer: "No. Returns depend on market value at the time of sale or resale. Past appreciation trends are informative but not a guarantee of future performance.",
      icon: TrendingUp
    },
    {
      question: "How are profits distributed?",
      answer: "Once land is sold: Sale proceeds → Escrow → Deduct platform fee → Remaining distributed among investors based on fractions owned. Payouts are transferred directly to investor bank accounts.",
      icon: DollarSign
    },
    {
      question: "What taxes apply?",
      answer: "Profits from resale or final sale are subject to capital gains tax as per Indian law. Investors are responsible for reporting and paying applicable taxes. (We provide payout statements to help with filing).",
      icon: FileText
    },
    {
      question: "Can NRIs invest?",
      answer: "Yes, NRIs can invest subject to Indian property and FEMA regulations. Payments must be made via authorized NRE/NRO accounts.",
      icon: MapPin
    },
    {
      question: "What are the main risks in fractional ownership of land?",
      answer: "The key risks are: Market Risk → Land prices may fluctuate depending on demand/supply. Regulatory Risk → Government or DDA policies may change and impact timelines. Execution Risk → Delays in land pooling, approvals, or project development can postpone exit. Liquidity Risk → While fractions can be resold, finding a buyer may sometimes take longer.",
      icon: AlertTriangle
    },
    {
      question: "What happens if the project faces delays?",
      answer: "If the land pooling or sale process is delayed, your investment remains tied up for longer than initially expected. Investors are updated regularly, and the SPV continues to hold the property securely until sale.",
      icon: Clock
    },
    {
      question: "What if the land doesn’t appreciate in value?",
      answer: "Like any investment, real estate values can stagnate or fall. While land is generally more stable than volatile assets, there is no guarantee of appreciation. Exit value will depend on prevailing market conditions.",
      icon: TrendingUp
    },
    {
      question: "Can I lose money in fractional ownership?",
      answer: "Yes. If the land is sold at a lower price than purchase cost, investors may receive less than what they invested (after fees). That’s why we encourage long-term holding and diversification across multiple projects.",
      icon: AlertTriangle
    },
    {
      question: "How does the platform reduce risks?",
      answer: "All projects go through legal due diligence and title verification. Funds are held in escrow accounts until transaction completion. SPVs ensure transparent ownership structure. Regular updates keep investors informed about timelines and market conditions.",
      icon: Shield
    },
    {
      question: "What if I need to exit early but cannot find a buyer?",
      answer: "The secondary resale marketplace is available, but liquidity depends on demand. If you can’t find a buyer immediately, you will need to hold your fractions until another investor is available or until the project’s final exit.",
      icon: Clock
    }
  ];

  return (
    <div className="homeContainer" style={{paddingTop: '4rem'}}>
      <section className="faq bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center mb-16">
            <h1 className="sectionTitle">❓ Frequently Asked Questions (FAQs)</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Everything you need to know about fractional real estate investment on FraxLandShare.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {faqs.map((faq, index) => {
              const IconComponent = faq.icon;
              return (
                <div key={index} className="faq-item bg-card/50 backdrop-blur-sm rounded-2xl p-6 border border-border/50 hover:border-primary/20 transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h2 className="faq-question text-lg font-semibold text-foreground mb-3">{index + 1}. {faq.question}</h2>
                      <p className="faq-answer text-muted-foreground leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
